import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { config } from "@/config/environment";
import { MsalService } from "@/components/security/msal-service";
import type { GetUserProfileResponse } from "@/domain/core";
import { authLogger } from "./auth-logger";
import { tokenClaimExtractor } from "./token-validator";
import { useAuth } from "@/hooks";
import { ROLE_CLAIM_RETRY_ATTEMPTS, ROLE_CLAIM_RETRY_BASE_DELAY_MS } from "@/constants";
import { delay } from "./app-utils";

// ============================================================================
// POST REGISTRATION HANDLER
// ============================================================================

export interface PostRegistrationResult {
  success: boolean;
  error?: string;
  userProfile?: GetUserProfileResponse;
}

/**
 * Handles the post-registration flow including token refresh and user profile update.
 * The sign-in access token predates Entra group assignment; subsequent
 * RequireAnyRole calls 403 until a cache-skipping refresh issues a new AT.
 */
export async function handlePostRegistrationFlow(
  refreshAccessToken: (options?: { forceRefresh?: boolean }) => Promise<{
    success: boolean;
    error?: string;
  }>,
  acquireAccessToken: (options?: {
    forceRefresh?: boolean;
  }) => Promise<string | null>,
  userName: string
): Promise<PostRegistrationResult> {
  try {
    if (config.debug.enabled) {
      authLogger.info("Starting post-registration flow", {
        account: userName
      });
    }

    let gotFreshToken = false;
    let lastRefreshError: string | undefined;

    for (let attempt = 1; attempt <= ROLE_CLAIM_RETRY_ATTEMPTS; attempt++) {
      const tokenResult = await refreshAccessToken({ forceRefresh: true });
      if (!tokenResult.success) {
        lastRefreshError = tokenResult.error;
        if (config.debug.enabled) {
          authLogger.warn("Post-registration token refresh attempt failed", {
            attempt,
            error: tokenResult.error
          });
        }
      } else {
        gotFreshToken = true;
        const accessToken = await acquireAccessToken();
        if (
          accessToken &&
          tokenClaimExtractor.hasAuthorizationClaims(accessToken)
        ) {
          if (config.debug.enabled) {
            authLogger.info("Token refresh completed with role claims", {
              attempt
            });
          }
          return { success: true };
        }

        if (config.debug.enabled) {
          authLogger.info(
            "Fresh token still missing role/group claims; retrying",
            { attempt }
          );
        }
      }

      if (attempt < ROLE_CLAIM_RETRY_ATTEMPTS) {
        await delay(ROLE_CLAIM_RETRY_BASE_DELAY_MS * attempt);
      }
    }

    if (!gotFreshToken) {
      return {
        success: false,
        error: `Token refresh failed: ${lastRefreshError ?? "Unknown error"}`
      };
    }

    // Cached pre-registration AT was replaced even if Entra has not yet
    // emitted groups. Dashboard calls use that last force-refreshed token.
    if (config.debug.enabled) {
      authLogger.warn(
        "Proceeding after post-registration refresh without confirmed role claims"
      );
    }

    return { success: true };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";

    if (config.debug.enabled) {
      authLogger.error("Post-registration flow failed", {
        error: errorMessage
      });
    }

    return {
      success: false,
      error: errorMessage
    };
  }
}

/**
 * Hook to handle post-registration flow
 */
export function usePostRegistrationHandler() {
  const { instance } = useMsal();
  const { refreshAccessToken } = useAuth();
  const msalService = MsalService.getInstance(instance);

  const handlePostRegistration =
    useCallback(async (): Promise<PostRegistrationResult> => {
      const account = msalService.getActiveAccount();
      if (!account) {
        return {
          success: false,
          error: "No active account found"
        };
      }

      return await handlePostRegistrationFlow(
        refreshAccessToken,
        (options) => msalService.acquireTokenSilent(account, options),
        account.username
      );
    }, [msalService, refreshAccessToken]);

  return {
    handlePostRegistration
  };
}
